import React from 'react';
import './Pagination.css';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalElements: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

/**
 * Pagination Component
 * Provides navigation controls and page size selector for paginated data
 */
export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  pageSize,
  totalElements,
  onPageChange,
  onPageSizeChange,
}) => {
  const pageSizeOptions = [10, 25, 50, 100];

  const handleFirstPage = () => {
    if (currentPage > 0) {
      onPageChange(0);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      onPageChange(currentPage + 1);
    }
  };

  const handleLastPage = () => {
    if (currentPage < totalPages - 1) {
      onPageChange(totalPages - 1);
    }
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value, 10);
    onPageSizeChange(newSize);
    // Reset to first page when changing page size
    if (currentPage > 0) {
      onPageChange(0);
    }
  };

  // Calculate the range of items being displayed
  const startItem = totalElements === 0 ? 0 : currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, totalElements);

  return (
    <div className="pagination">
      <div className="pagination-info">
        <span className="pagination-count">
          Showing {startItem}-{endItem} of {totalElements} tasks
        </span>
        <div className="page-size-selector">
          <label htmlFor="page-size">Items per page:</label>
          <select
            id="page-size"
            value={pageSize}
            onChange={handlePageSizeChange}
            className="page-size-select"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="pagination-controls">
        <button
          onClick={handleFirstPage}
          disabled={currentPage === 0}
          className="pagination-button"
          aria-label="First page"
          title="First page"
        >
          ⟪
        </button>
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 0}
          className="pagination-button"
          aria-label="Previous page"
          title="Previous page"
        >
          ‹
        </button>

        <span className="pagination-page-info">
          Page {currentPage + 1} of {totalPages || 1}
        </span>

        <button
          onClick={handleNextPage}
          disabled={currentPage >= totalPages - 1 || totalPages === 0}
          className="pagination-button"
          aria-label="Next page"
          title="Next page"
        >
          ›
        </button>
        <button
          onClick={handleLastPage}
          disabled={currentPage >= totalPages - 1 || totalPages === 0}
          className="pagination-button"
          aria-label="Last page"
          title="Last page"
        >
          ⟫
        </button>
      </div>
    </div>
  );
};
